import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  Suspense,
  useMemo
} from 'react';
import { Polygon, Polyline } from '@react-google-maps/api';
import { GoogleMapsComponentType } from './shapes.types';
import {
  clearPolygon,
  completePolygon,
  getBoundaryBox,
  getCentroid,
  isPolygonInvalid,
  showError,
  showPopUp
} from '../../CommonUtilities/CommonUtilities';
import { STRINGS } from '../../Constants/ConstantStrings';
import {
  MapComponentWrapper,
  DrawControlButtonsWrapper
} from '../globalStyles';
import { btnStyles } from '../../Universal/NovusDSImports/variants';
import {
  MapCursor,
  CursorIcon,
  VectorBlackIcon,
  VectorThemeIcon
} from '../../Universal/Assets';
import PALoader from '../../SharedComponets/PALoader';
import { useReduxSelector } from '../../Store/reduxHooks';
import { RootState } from '../../store';
import { TooltipComponent } from '../../SharedComponets/Tooltip';
import Map from '../../SharedComponets/Map/Map';
import SearchField from '../../SharedComponets/Map/SearchField';
import { usePolygonDrawing } from '../../Hooks/UsePolygonDrawing';

const GoogleMapsComponent: React.FC<GoogleMapsComponentType> = ({
  editModeData,
  editModeType,
  drawnPolygon,
  isDrawing,
  setIsDrawing,
  setDrawnPolygon
}) => {
  const mapRef = useRef<google.maps.Map | null>(null);
  const polygonRef = useRef<google.maps.Polygon | null>(null);
  const listenersRef = useRef<Array<google.maps.MapsEventListener>>([]);

  const searchBoxRef = useRef<any>(null);

  const [center, setCenter] = useState<{ lat: any; lng: any }>({
    lat: 0,
    lng: 0
  });

  const [drawingPoints, setDrawingPoints] = useState<Array<[number, number]>>(
    []
  );
  const [hoverPoint, setHoverPoint] = useState<{
    lat: number;
    lng: number;
  } | null>(null);

  const stateBoundary = useReduxSelector(
    (state: RootState) => state.ClientInfo.state_boundary
  );

  const [zoomLevel, setZoomLevel] = useState<number>(2);
  const [isDrawMode, setIsDrawMode] = useState<boolean>(false);
  const [showClearButton, setShowClearButton] = useState<boolean>(false);
  const isMapLoaded = useReduxSelector(
    (state: RootState) => state.Common.isMapLoaded
  );

  const { isPointNearFirstPoint, handleDrawingCursorChange } =
    usePolygonDrawing({
      mapRef,
      isDrawing,
      isDrawMode
    });

  const onLoad = useCallback(
    (map) => {
      const bounds = new window.google.maps.LatLngBounds();
      if (editModeData && editModeData.geometry && editModeType !== 'Add') {
        editModeData.geometry.coordinates[0].forEach((point: any) => {
          bounds.extend({ lat: point[0], lng: point[1] });
        });
        const value = getCentroid(editModeData.geometry.coordinates[0]);
        map.fitBounds(bounds);
        setCenter({ lat: value[1], lng: value[0] });
      } else if (stateBoundary) {
        const boundary = getBoundaryBox(stateBoundary.coordinates);
        bounds.extend({ lat: boundary[1], lng: boundary[0] });
        bounds.extend({ lat: boundary[3], lng: boundary[0] });
        bounds.extend({ lat: boundary[3], lng: boundary[2] });
        bounds.extend({ lat: boundary[1], lng: boundary[2] });
        map.fitBounds(bounds);
      }
      map.setOptions({
        draggableCursor: isDrawMode ? 'crosshair' : undefined,
        draggingCursor: isDrawMode ? 'crosshair' : undefined
      });
      mapRef.current = map;
    },
    [editModeData, editModeType, stateBoundary, isDrawMode]
  );

  useEffect(() => {
    if (!mapRef.current) return;

    mapRef.current.setOptions({
      draggableCursor: isDrawMode ? 'crosshair' : undefined,
      draggingCursor: isDrawMode ? 'crosshair' : undefined
    });
    if (!isDrawMode) {
      setHoverPoint(null);
    }
  }, [isDrawMode]);

  useEffect(() => {
    if (stateBoundary) {
      const centerPoint = getCentroid(stateBoundary.coordinates[0]);

      setCenter({
        lat: centerPoint[0],
        lng: centerPoint[1]
      });

      setZoomLevel(8);
    }
  }, [stateBoundary, setCenter]);

  useEffect(() => {
    if (editModeData && editModeData.geometry && editModeType !== 'Add') {
      setDrawnPolygon(editModeData.geometry.coordinates[0]);
      setShowClearButton(true);
    }
  }, [editModeData, setDrawnPolygon, editModeType]);

  const onUnmount = useCallback((map) => {
    mapRef.current = null;
  }, []);

  const getPolygonPath = (coords: any) => {
    const path: any = [];
    coords &&
      coords.forEach((point: any) => {
        path.push({ lat: point[0], lng: point[1] });
      });
    return path;
  };

  const handleClear = () => {
    clearPolygon(polygonRef.current);
    setDrawnPolygon(null);
    setDrawingPoints([]);
    setHoverPoint(null);
    setShowClearButton(false);
    setIsDrawing(true);
  };

  const handleAddPoint = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (!isDrawing || !isDrawMode || !event.latLng) return;

      const point: [number, number] = [event.latLng.lat(), event.latLng.lng()];

      setDrawingPoints((prev) => [...prev, point]);
    },
    [isDrawing, isDrawMode]
  );

  const handleCompletePolygon = useCallback(() => {
    if (!isDrawing || !isDrawMode) return;

    if (drawingPoints.length < 3) {
      showPopUp('Please add at least 3 points', 'error');
      return;
    }

    const polygonCoords = completePolygon([...drawingPoints]);

    if (stateBoundary?.coordinates) {
      const polygonInvalidError = isPolygonInvalid(
        polygonCoords,
        true,
        stateBoundary.coordinates
      );
      if (polygonInvalidError) {
        handleClear();
        showPopUp(polygonInvalidError, 'error');
        return;
      }
    } else {
      showError(STRINGS.SOMETHING_WENT_WRONG_PLEASE_TRY_AGAIN);
    }

    setDrawnPolygon(polygonCoords);
    setDrawingPoints([]);
    setHoverPoint(null);
    setShowClearButton(true);
    setIsDrawMode(false);
    setIsDrawing(false);
  }, [drawingPoints, isDrawing, isDrawMode, setDrawnPolygon, stateBoundary]);

  const handleMouseMove = useCallback(
    (event: google.maps.MapMouseEvent) => {
      if (!isDrawing || !isDrawMode || !event.latLng) {
        setHoverPoint(null);
        return;
      }

      const lat = event.latLng.lat();
      const lng = event.latLng.lng();

      handleDrawingCursorChange(lat, lng, drawingPoints);

      setHoverPoint({
        lat: event.latLng.lat(),
        lng: event.latLng.lng()
      });
    },
    [isDrawing, isDrawMode, handleDrawingCursorChange, drawingPoints]
  );

  const onEdit = useCallback(() => {
    if (polygonRef.current) {
      const nextPath: any = [];
      polygonRef.current
        .getPath()
        .getArray()
        .forEach((latLng) => {
          nextPath.push([latLng.lat(), latLng.lng()]);
        });
      setDrawnPolygon(nextPath);
    }
  }, [setDrawnPolygon]);

  const onPolygonLoad = useCallback(
    (polygon) => {
      polygonRef.current = polygon;
      const path = polygon.getPath();
      if (editModeType !== 'View')
        listenersRef.current.push(
          path?.addListener('set_at', () => onEdit()),
          path?.addListener('insert_at', () => onEdit()),
          path?.addListener('remove_at', () => onEdit())
        );
    },
    [onEdit, editModeType]
  );

  const mapContainerStyle = useMemo(
    () => ({
      width: '100%',
      height: '100%',
      borderRadius: 8
    }),
    []
  );

  const mapOptions = useMemo(
    () => ({
      mapTypeControl: true,
      streetViewControl: false
    }),
    []
  );

  const handleMapClick = useCallback(
    (event: google.maps.MapMouseEvent) => {
      event.stop();

      if (!isDrawing || !isDrawMode || !event.latLng) return;

      const lat = event.latLng.lat();
      const lng = event.latLng.lng();

      if (isPointNearFirstPoint(lat, lng, drawingPoints)) {
        handleCompletePolygon();
        return;
      }

      handleAddPoint(event);
    },
    [
      handleAddPoint,
      isDrawing,
      isDrawMode,
      handleCompletePolygon,
      isPointNearFirstPoint,
      drawingPoints
    ]
  );

  return (
    <MapComponentWrapper
      id="map-wrapper"
      onClick={(e) => {
        if (isDrawing && isDrawMode && e.detail === 2) {
          handleCompletePolygon();
        }
      }}
    >
      <Suspense fallback={<PALoader />}>
        <Map
          id="PolygonPreviewModal"
          containerStyle={mapContainerStyle}
          center={center}
          onLoad={onLoad}
          onUnmount={onUnmount}
          zoom={zoomLevel}
          onClick={handleMapClick}
          onMouseMove={handleMouseMove}
          options={{
            ...mapOptions,
            draggableCursor: isDrawMode ? 'crosshair' : undefined
          }}
        >
          <div className="polygon-buttons-wrapper col-xl-10">
            <SearchField setMapCenter={setCenter} setZoomLevel={setZoomLevel} />

            {isDrawing && (
              <DrawControlButtonsWrapper
                isDrawMode={isDrawMode}
                className="draw-control-buttons"
              >
                <div
                  onClick={() => {
                    if (isDrawMode) {
                      setIsDrawMode(false);
                      setDrawingPoints([]);
                    }
                  }}
                  onKeyDown={(event: any) => {
                    if (event.key === 'Enter') {
                      setIsDrawMode(false);
                      setDrawingPoints([]);
                    }
                  }}
                  aria-label="cursor-icon"
                  className={`${!isDrawMode && 'cursor-btn'} p-2 cursor-toggle`}
                  tabIndex={0}
                  data-tooltip-id="map-cursor"
                >
                  <TooltipComponent
                    id="map-cursor"
                    place="bottom-end"
                    tooltipBgColor="${colorState.surface.default['primary-inverse']}"
                    arrowColor="${colorState.surface.default['primary-inverse']}"
                  >
                    Map Cursor
                  </TooltipComponent>
                  <img
                    src={!isDrawMode ? MapCursor : CursorIcon}
                    alt={'cursor-img'}
                    className="map-cursor-icon"
                  />
                </div>
                <div
                  {...btnStyles.secondary}
                  onClick={() => {
                    setIsDrawMode(true);
                  }}
                  onKeyDown={(event: any) => {
                    if (event.key === 'Enter') {
                      setIsDrawMode(true);
                    }
                  }}
                  aria-label="draw-polygon"
                  className={`${
                    isDrawMode && 'draw-polygon-btn'
                  } p-2 cursor-toggle`}
                  tabIndex={0}
                  data-tooltip-id="create-shape"
                >
                  <TooltipComponent
                    id="create-shape"
                    place="bottom-start"
                    tooltipBgColor="${colorState.surface.default['primary-inverse']}"
                    arrowColor="${colorState.surface.default['primary-inverse']}"
                  >
                    Create Shapes
                  </TooltipComponent>

                  <img
                    src={isDrawMode ? VectorThemeIcon : VectorBlackIcon}
                    alt="vector-black-icon"
                  />
                </div>
              </DrawControlButtonsWrapper>
            )}
            {showClearButton && (
              <div
                onClick={handleClear}
                className="polygon-clear-button"
                tabIndex={0}
              >
                {STRINGS.CLEAR}
              </div>
            )}
          </div>

          {isDrawing && isDrawMode && drawingPoints.length > 0 && (
            <Polyline
              path={
                hoverPoint
                  ? [
                      ...drawingPoints.map((p) => ({ lat: p[0], lng: p[1] })),
                      hoverPoint
                    ]
                  : drawingPoints.map((p) => ({ lat: p[0], lng: p[1] }))
              }
              options={{
                strokeColor: 'red',
                strokeOpacity: 1,
                strokeWeight: 2,
                clickable: false,
                editable: false,
                draggable: false,
                zIndex: 450
              }}
            />
          )}

          {showClearButton && (
            <div
              onClick={handleClear}
              className="polygon-clear-button"
              tabIndex={0}
            >
              {STRINGS.CLEAR}
            </div>
          )}
          {drawnPolygon && (
            <Polygon
              onLoad={onPolygonLoad}
              paths={getPolygonPath(drawnPolygon)}
              options={{
                fillColor: 'red',
                fillOpacity: 0.2,
                strokeWeight: 2,
                strokeColor: 'red',
                clickable: false,
                zIndex: 300,
                editable: editModeType === 'Edit'
              }}
              onMouseUp={onEdit}
            />
          )}
        </Map>
      </Suspense>
    </MapComponentWrapper>
  );
};

export default GoogleMapsComponent;
